import { ExportButton } from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiClient,
  useApiMutation,
  useDashboard,
  useTeam,
} from '@vrplatform/data';
import {
  hideNotification,
  showLoadingNotification,
  showWarnNotification,
  updateSuccessNotification,
  updateWarnNotification,
} from '@vrplatform/theme';
import {
  getBalanceSheetQueryFilterParams,
  getGuestBalancesQueryParams,
  getJournalEntriesApiQueryFilter,
  getOwnerStatementSummariesDetailQueryFilterParams,
  getOwnerStatementSummariesQueryFilterParams,
  getOwnerStatementSummariesDetailTransactionLookup,
  useTableStoreAllPagesSelected,
  useTableStoreSelected,
  ownerStatementSummariesMonthRangeLabelFormatter,
  getProfitAndLossQueryFilterParams,
  getTrialBalanceQueryFilterParams,
  getTrustReconciliationByListingQueryFilterParams,
  type OwnerStatementSummariesDetailData,
  useOwnerStatementSummariesDetailQuery,
  useOwnerStatementSummariesDetailTransactionsQuery,
  useOwnerStatementSummariesTableQuery,
  useBalanceSheetTableFilter,
  useGuestBalancesTableFilter,
  useJournalEntriesTableFilter,
  useOwnerStatementSummariesDetailFilter,
  useProfitAndLossTableFilter,
  useSalesTaxLiabilityTableFilter,
  useTrialBalanceTableFilter,
  useTrustReconciliationByListingTableFilter,
  useTrustReconcilationTableFilter,
  augmentOwnerStatementSummariesDetailDataWithTransactions,
  mapOwnerStatementSummariesDetailTransactionsById,
} from '@vrplatform/embed';
import {
  type ComponentProps,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { chunkArray } from '@vrplatform/utils';
import {
  createZipBlob,
  downloadBlob,
  downloadFromUrl,
  renderPdfToBlob,
} from '../../utils';
import {
  useJournalEntriesModalFilter,
  useJournalEntriesModalWhere,
} from '../../modals';
import { OwnerStatementSummariesBulkPdfExportModal } from './owner-statement-summaries/OwnerStatementSummariesBulkPdfExportModal';
import { getOwnerStatementSummaryPdfModel } from './owner-statement-summaries/pdf/OwnerStatementSummaryPdf.utils';
import { OwnerStatementSummaryPdfDocument } from './owner-statement-summaries/pdf/OwnerStatementSummaryPdfDocument';

export type Report =
  | 'detail'
  | 'trial-balance'
  | 'trust-reconciliation'
  | 'trust-reconciliation-by-listing'
  | 'guest-balances'
  | 'sales-tax-liability'
  | 'profit-and-loss'
  | 'statement-summaries'
  | 'balance-sheet';

type Props = {
  report: Report;
  disabled?: boolean;
};

export const GeneralLedgerExportButton = (props: Props) => {
  'use-memo';

  const { download, loading, disabled } = useDownloadCsv(props);
  const { filter } = useOwnerStatementSummariesDetailFilter();

  if (props.report === 'statement-summaries' && !filter.contactId) {
    return <OwnerStatementSummariesListExportButton />;
  }

  if (props.report === 'statement-summaries') {
    return (
      <OwnerStatementSummariesDetailExportButton disabled={props.disabled} />
    );
  }

  return (
    <ExportButton
      onClick={() => download()}
      variant="default"
      loading={loading}
      disabled={props.disabled || disabled}
    />
  );
};

const OwnerStatementSummariesDetailExportButton = ({
  disabled,
}: Pick<Props, 'disabled'>) => {
  const { download: downloadCsv, loading: csvLoading } = useDownloadCsv({
    report: 'statement-summaries',
  });
  const {
    downloadPdf,
    openPdfInNewTab,
    loading: pdfLoading,
  } = useDownloadOwnerStatementSummaryPdf();

  const menuItems = useMemo<
    NonNullable<ComponentProps<typeof ExportButton>['menuItems']>
  >(
    () => [
      {
        label: 'Download CSV',
        onClick: () => downloadCsv(),
        icon: 'FileTextIcon',
      },
      {
        label: 'Open PDF in new tab',
        onClick: () => openPdfInNewTab(),
        icon: 'ExternalLinkIcon',
      },
      {
        label: 'Download PDF',
        onClick: () => downloadPdf(),
        icon: 'FileDownloadIcon',
      },
    ],
    [downloadCsv, downloadPdf, openPdfInNewTab]
  );

  return (
    <ExportButton
      variant="default"
      menuItems={menuItems}
      loading={csvLoading || pdfLoading}
      disabled={disabled}
    />
  );
};

const OwnerStatementSummariesListExportButton = () => {
  const [bulkPdfModalOpened, setBulkPdfModalOpened] = useState(false);
  const {
    download: downloadPdf,
    loading: pdfLoading,
    rowCount,
  } = useDownloadOwnerStatementSummariesListPdf();
  const { filter: summariesDetailFilter } =
    useOwnerStatementSummariesDetailFilter();
  const selected = useTableStoreSelected();
  const allPagesSelected = useTableStoreAllPagesSelected();

  const [loading, setLoading] = useState(false);
  const notifyId = useId();

  const $api = useApiClient();

  const download = useCallback(
    async (type: 'normal' | 'detail') => {
      showLoadingNotification({ id: notifyId });
      setLoading(true);
      try {
        const query = getOwnerStatementSummariesQueryFilterParams(
          summariesDetailFilter.monthRange
        );

        // NORMAL DETAIL
        if (type === 'normal') {
          await $api
            .GET('/reports/owner-statement-summaries-per-layout/csv', {
              params: {
                query,
              },
            })
            .then((res) => {
              if (res.error) throw res.error;
              const url = res.data.url;
              downloadFromUrl(url, 'owner-statement-summaries');
            });
        } else {
          await $api
            .GET('/reports/owner-statement-summaries-per-layout/csv/detailed', {
              params: {
                query,
              },
            })
            .then((res) => {
              if (res.error) throw res.error;
              const url = res.data.url;
              downloadFromUrl(url, 'owner-statement-summaries');
            });
        }

        updateSuccessNotification({
          id: notifyId,
          title: 'CSV Download',
          message: 'CSV was successfully downloaded.',
        });
      } catch (error) {
        showApiErrorNotification({
          id: notifyId,
          error,
          title: "Couldn't download CSV",
          defaultMessage:
            'We failed to download the CSV. Please contact support if the issue persists.',
        });
      }
      setLoading(false);
    },
    [summariesDetailFilter, $api, notifyId]
  );

  const menuItems = useMemo<
    NonNullable<ComponentProps<typeof ExportButton>['menuItems']>
  >(
    () => [
      {
        label: 'List of summaries',
        onClick: () => download('normal'),
        icon: 'FileDownloadIcon',
      },
      {
        label: 'Detailed summaries',
        onClick: () => download('detail'),
        icon: 'FileTextIcon',
      },
      {
        label: 'Download PDFs',
        onClick: () => setBulkPdfModalOpened(true),
        icon: 'FileDownloadIcon',
      },
    ],
    [download]
  );

  const selectedContactIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, isSelected]) => !!isSelected)
        .map(([contactId]) => contactId),
    [selected]
  );

  const selectedCount = allPagesSelected ? rowCount : selectedContactIds.length;

  return (
    <>
      <ExportButton
        variant="default"
        menuItems={menuItems}
        loading={loading || pdfLoading}
      />
      <OwnerStatementSummariesBulkPdfExportModal
        opened={bulkPdfModalOpened}
        onClose={() => setBulkPdfModalOpened(false)}
        onSubmit={async (layoutId) => {
          await downloadPdf({
            layoutId,
            contactIds: allPagesSelected ? undefined : selectedContactIds,
          });
        }}
        loading={pdfLoading}
        selectedCount={selectedCount}
        totalCount={rowCount}
      />
    </>
  );
};

const hasOwnerStatementSummariesDetailTransactionLookup = ({
  ids,
  shortRefs,
  uniqueRefs,
}: ReturnType<typeof getOwnerStatementSummariesDetailTransactionLookup>) =>
  ids.length > 0 || shortRefs.length > 0 || uniqueRefs.length > 0;

const fetchOwnerStatementSummariesDetailTransactionsById = async ({
  apiClient,
  lookup,
}: {
  apiClient: ReturnType<typeof useApiClient>;
  lookup: ReturnType<typeof getOwnerStatementSummariesDetailTransactionLookup>;
}) => {
  const transactions = await Promise.all([
    lookup.ids.length
      ? apiClient
          .GET('/transactions', {
            params: {
              query: {
                ids: lookup.ids.join(','),
                includeLines: true,
                limit: lookup.ids.length,
              },
            },
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data.data;
          })
      : [],
    lookup.shortRefs.length
      ? apiClient
          .GET('/transactions', {
            params: {
              query: {
                shortRefs: lookup.shortRefs.join(','),
                includeLines: true,
                limit: lookup.shortRefs.length,
              },
            },
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data.data;
          })
      : [],
    lookup.uniqueRefs.length
      ? apiClient
          .GET('/transactions', {
            params: {
              query: {
                uniqueRefs: lookup.uniqueRefs.join(','),
                includeLines: true,
                limit: lookup.uniqueRefs.length,
              },
            },
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data.data;
          })
      : [],
  ]).then((results) => results.flat());

  return mapOwnerStatementSummariesDetailTransactionsById({
    transactions,
  });
};

function useDownloadOwnerStatementSummaryPdf() {
  const notifyId = useId();
  const [loading, setLoading] = useState(false);
  const { filter } = useOwnerStatementSummariesDetailFilter();
  const queryData = useOwnerStatementSummariesDetailQuery();
  const transactionLookup = useMemo(() => {
    if (!queryData.data) {
      return {
        ids: [],
        shortRefs: [],
        uniqueRefs: [],
      };
    }

    return getOwnerStatementSummariesDetailTransactionLookup({
      data: queryData.data,
      view: filter.view,
    });
  }, [filter.view, queryData.data]);
  const transactionDetailsQuery =
    useOwnerStatementSummariesDetailTransactionsQuery({
      lookup: transactionLookup,
    });
  const hasTransactionLookup =
    hasOwnerStatementSummariesDetailTransactionLookup(transactionLookup);

  const exportPdf = useCallback(
    async (disposition: 'download' | 'openInNewTab') => {
      if (
        queryData.isLoading ||
        queryData.isFetching ||
        (hasTransactionLookup &&
          (transactionDetailsQuery.isLoading ||
            transactionDetailsQuery.isFetching))
      ) {
        showWarnNotification({
          id: notifyId,
          message: 'Please wait for the statement summary to finish loading.',
        });
        return;
      }

      if (queryData.error) {
        showApiErrorNotification({
          id: notifyId,
          error: queryData.error,
          title: "Couldn't export PDF",
          defaultMessage:
            'We failed to prepare the PDF. Please contact support if the issue persists.',
        });
        return;
      }

      if (hasTransactionLookup && transactionDetailsQuery.error) {
        showApiErrorNotification({
          id: notifyId,
          error: transactionDetailsQuery.error,
          title: "Couldn't export PDF",
          defaultMessage:
            'We failed to load the statement summary details. Please contact support if the issue persists.',
        });
        return;
      }

      if (!queryData.data || !queryData.layoutId) {
        showWarnNotification({
          id: notifyId,
          message: 'No statement summary is available to export as PDF.',
        });
        return;
      }

      setLoading(true);
      showLoadingNotification({ id: notifyId });

      try {
        const data =
          hasTransactionLookup && transactionDetailsQuery.data
            ? augmentOwnerStatementSummariesDetailDataWithTransactions({
                data: queryData.data,
                transactionsById: transactionDetailsQuery.data,
                view: filter.view,
              })
            : queryData.data;
        const model = getOwnerStatementSummaryPdfModel({
          currency: data.currency,
          data,
          layoutId: queryData.layoutId,
          monthRange: filter.monthRange,
        });

        const blob = await renderPdfToBlob(
          <OwnerStatementSummaryPdfDocument model={model} />
        );

        downloadBlob(blob, `${model.filename}.pdf`, {
          disposition,
        });

        updateSuccessNotification({
          id: notifyId,
          title: 'PDF Export',
          message:
            disposition === 'openInNewTab'
              ? 'PDF opened in a new tab.'
              : 'PDF downloaded successfully.',
        });
      } catch (error) {
        showApiErrorNotification({
          id: notifyId,
          error,
          title: "Couldn't export PDF",
          defaultMessage:
            'We failed to prepare the PDF. Please contact support if the issue persists.',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      filter.monthRange,
      filter.view,
      hasTransactionLookup,
      notifyId,
      queryData.data,
      queryData.error,
      queryData.isFetching,
      queryData.isLoading,
      queryData.layoutId,
      transactionDetailsQuery.data,
      transactionDetailsQuery.error,
      transactionDetailsQuery.isFetching,
      transactionDetailsQuery.isLoading,
    ]
  );

  return {
    downloadPdf: () => exportPdf('download'),
    openPdfInNewTab: () => exportPdf('openInNewTab'),
    loading,
  };
}

const OWNER_STATEMENT_SUMMARY_PDF_BATCH_SIZE = 5;

type DownloadableFile = {
  blob: Blob;
  filename: string;
};

function useDownloadOwnerStatementSummariesListPdf() {
  const notifyId = useId();
  const [loading, setLoading] = useState(false);
  const [{ id: teamId }] = useTeam();
  const [dashboard] = useDashboard();
  const { filter } = useOwnerStatementSummariesDetailFilter();
  const queryData = useOwnerStatementSummariesTableQuery();
  const $api = useApiClient();

  const download = useCallback(
    async ({
      contactIds,
      layoutId,
    }: {
      contactIds?: string[];
      layoutId: string;
    }) => {
      if (queryData.isLoading || queryData.isFetching) {
        showWarnNotification({
          id: notifyId,
          message: 'Please wait for the statement summaries to finish loading.',
        });
        return;
      }

      if (queryData.error) {
        showApiErrorNotification({
          id: notifyId,
          error: queryData.error,
          title: "Couldn't download PDFs",
          defaultMessage:
            'We failed to download the PDFs. Please contact support if the issue persists.',
        });
        return;
      }

      const rows = queryData.data?.rows ?? [];
      const selectedRows = contactIds?.length
        ? rows.filter((row) => contactIds.includes(row.contact.contactId))
        : rows;

      if (!selectedRows.length) {
        showWarnNotification({
          id: notifyId,
          message: 'No statement summaries are available to export as PDFs.',
        });
        return;
      }

      setLoading(true);
      showLoadingNotification({ id: notifyId });

      try {
        const team = await $api
          .GET('/teams/{id}', {
            params: {
              path: {
                id: teamId,
              },
            },
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data;
          });

        const files: DownloadableFile[] = [];
        const failures: { contactId: string; error: unknown }[] = [];

        for (const batch of chunkArray(
          selectedRows,
          OWNER_STATEMENT_SUMMARY_PDF_BATCH_SIZE
        )) {
          const results = await Promise.allSettled(
            batch.map(async (row) => {
              const contactId = row.contact.contactId;

              const [contact, report] = await Promise.all([
                $api
                  .GET('/contacts/{id}', {
                    params: {
                      path: {
                        id: contactId,
                      },
                    },
                  })
                  .then((res) => {
                    if (res.error) throw res.error;
                    return res.data;
                  }),
                $api
                  .GET(
                    '/reports/owner-statement-summaries-per-layout/details/{contactId}',
                    {
                      params: getOwnerStatementSummariesDetailQueryFilterParams(
                        {
                          filter: {
                            ...filter,
                            contactId,
                          },
                          dashboard,
                        }
                      ),
                    }
                  )
                  .then((res) => {
                    if (res.error) throw res.error;
                    return res.data;
                  }),
              ]);

              const data: OwnerStatementSummariesDetailData = {
                ...report,
                team,
                contact,
                listing: null,
                resolvedView: filter.view,
              };
              const transactionLookup =
                getOwnerStatementSummariesDetailTransactionLookup({
                  data,
                  view: filter.view,
                });
              const enrichedData =
                hasOwnerStatementSummariesDetailTransactionLookup(
                  transactionLookup
                )
                  ? augmentOwnerStatementSummariesDetailDataWithTransactions({
                      data,
                      transactionsById:
                        await fetchOwnerStatementSummariesDetailTransactionsById(
                          {
                            apiClient: $api,
                            lookup: transactionLookup,
                          }
                        ),
                      view: filter.view,
                    })
                  : data;

              const model = getOwnerStatementSummaryPdfModel({
                currency: enrichedData.currency,
                data: enrichedData,
                layoutId,
                monthRange: filter.monthRange,
              });

              const blob = await renderPdfToBlob(
                <OwnerStatementSummaryPdfDocument model={model} />
              );

              return {
                blob,
                filename: `${model.filename}.pdf`,
              } satisfies DownloadableFile;
            })
          );

          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              files.push(result.value);
              return;
            }

            failures.push({
              contactId: batch[index]!.contact.contactId,
              error: result.reason,
            });
          });
        }

        if (!files.length) {
          throw new Error('No statement summary PDFs were generated.');
        }

        if (files.length === 1) {
          downloadBlobFile(files[0]!);
        } else {
          await downloadZipFile({
            files,
            filename: getOwnerStatementSummariesPdfArchiveFilename(
              filter.monthRange
            ),
          });
        }

        if (failures.length > 0) {
          updateWarnNotification({
            id: notifyId,
            title: 'PDF Export',
            message:
              files.length === 1
                ? `PDF downloaded successfully. ${failures.length} ${
                    failures.length === 1 ? 'summary' : 'summaries'
                  } failed to export.`
                : `${files.length} PDFs downloaded. ${failures.length} ${
                    failures.length === 1 ? 'summary' : 'summaries'
                  } failed to export.`,
          });
          return;
        }

        updateSuccessNotification({
          id: notifyId,
          title: 'PDF Export',
          message:
            files.length === 1
              ? 'PDF downloaded successfully.'
              : `Downloaded ${files.length} PDFs successfully.`,
        });
      } catch (error) {
        showApiErrorNotification({
          id: notifyId,
          error,
          title: "Couldn't download PDFs",
          defaultMessage:
            'We failed to download the PDFs. Please contact support if the issue persists.',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      $api,
      dashboard,
      filter,
      notifyId,
      queryData.data?.rows,
      queryData.error,
      queryData.isFetching,
      queryData.isLoading,
      teamId,
    ]
  );

  return {
    download,
    loading,
    rowCount: queryData.data?.rows.length ?? 0,
  };
}

const sanitizeFilenamePart = (value: string) =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'owner_statement_summaries';

const getOwnerStatementSummariesPdfArchiveFilename = (
  monthRange: `${string}...${string}`
) =>
  `owner_statement_summaries_${sanitizeFilenamePart(
    ownerStatementSummariesMonthRangeLabelFormatter(monthRange)
  )}.zip`;

const downloadBlobFile = ({ blob, filename }: DownloadableFile) => {
  downloadBlob(blob, filename);
};

const downloadZipFile = async ({
  files,
  filename,
}: {
  files: DownloadableFile[];
  filename: string;
}) => {
  const blob = await createZipBlob(files);
  downloadBlobFile({
    blob,
    filename,
  });
};

function useDownloadCsv({ report: activeTab }: Props) {
  const notifyId = useId();
  const [dashboard] = useDashboard();

  const [loading, setLoading] = useState(false);

  const getJournalEntriesCsvUrl = useGetJournalEntriesCsvRows();

  const { filter: trustReconciliationFilter } =
    useTrustReconcilationTableFilter();
  const { filter: trustReconciliationByListingFilter } =
    useTrustReconciliationByListingTableFilter();
  const { filter: guestBalancesFilter } = useGuestBalancesTableFilter();
  const { filter: profitAndLossFilter } = useProfitAndLossTableFilter();
  const { filter: balanceSheetFilter } = useBalanceSheetTableFilter();
  const { filter: salesTaxLiabilityFilter } = useSalesTaxLiabilityTableFilter();
  const { filter: summariesDetailFilter } =
    useOwnerStatementSummariesDetailFilter();
  const { filter: trialBalanceFilter } = useTrialBalanceTableFilter();

  const $api = useApiClient();

  const { mutateAsync: getTrustReconciliationCsv } = useApiMutation(
    'get',
    '/reports/trust-reconciliation/csv'
  );

  const { mutateAsync: getTrustReconciliationByListingCsv } = useApiMutation(
    'get',
    '/reports/trust-reconciliation/by-listing/csv'
  );

  const { mutateAsync: getTrialBalanceCsv } = useApiMutation(
    'get',
    '/reports/trial-balance/csv'
  );

  const { mutateAsync: getGuestBalancesCsv } = useApiMutation(
    'get',
    '/reports/guest-balances/csv'
  );

  const { mutateAsync: getSalesTaxLiabilityCsv } = useApiMutation(
    'get',
    '/reports/sales-tax-liability/csv'
  );

  const download = async () => {
    setLoading(true);
    showLoadingNotification({ id: notifyId });

    try {
      let csv:
        | undefined
        | {
            url: string;
            title: string;
          }
        | undefined;

      if (activeTab === 'trial-balance') {
        const res = await getTrialBalanceCsv({
          params: {
            query: getTrialBalanceQueryFilterParams(trialBalanceFilter),
          },
        });

        csv = {
          url: res.data.url,
          title: 'trial-balance',
        };
      } else if (activeTab === 'detail') {
        const res = await getJournalEntriesCsvUrl();
        csv = {
          url: res.data.url,
          title: 'general-ledger',
        };
      } else if (activeTab === 'trust-reconciliation') {
        const res = await getTrustReconciliationCsv({
          params: {
            query: {
              endDate: trustReconciliationFilter.endDate,
              listingId: trustReconciliationFilter.listingId ?? undefined,
            },
          },
        });

        csv = {
          url: res.data.url,
          title: 'trust-reconciliation',
        };
      } else if (activeTab === 'trust-reconciliation-by-listing') {
        const res = await getTrustReconciliationByListingCsv({
          params: {
            query: getTrustReconciliationByListingQueryFilterParams(
              trustReconciliationByListingFilter
            ),
          },
        });

        csv = {
          url: res.data.url,
          title: 'trust-reconciliation-by-listing',
        };
      } else if (activeTab === 'guest-balances') {
        if (!guestBalancesFilter.occupancyStatus) {
          showWarnNotification({
            message: 'Please select an occupancy status',
            id: notifyId,
          });
          return;
        }

        const res = await getGuestBalancesCsv({
          params: {
            query: getGuestBalancesQueryParams(guestBalancesFilter),
          },
        });

        csv = {
          url: res.data.url,
          title: 'guest-balances',
        };
      } else if (activeTab === 'sales-tax-liability') {
        if (!salesTaxLiabilityFilter.taxableLineIds.length) {
          showWarnNotification({
            message:
              'Please select your reservation financials to calculate the taxable amount on each reservation',
            id: notifyId,
          });
          return;
        }

        const res = await getSalesTaxLiabilityCsv({
          params: {
            query: {
              startDate: salesTaxLiabilityFilter.startDate,
              endDate: salesTaxLiabilityFilter.endDate,
              taxableLineIds: salesTaxLiabilityFilter.taxableLineIds.join(','),
              channels:
                salesTaxLiabilityFilter.channels?.join(',') ?? undefined,
            },
          },
        });

        csv = {
          url: res.data.url,
          title: 'sales-tax-liability',
        };
      } else if (activeTab === 'profit-and-loss') {
        const res = await $api.GET('/reports/profit-and-loss/csv', {
          params: {
            query: getProfitAndLossQueryFilterParams(profitAndLossFilter),
          },
        });
        if (res.error) throw res.error;
        csv = {
          url: res.data.url,
          title: 'profit-and-loss',
        };
      } else if (activeTab === 'statement-summaries') {
        let url: string | undefined;

        if (summariesDetailFilter.contactId) {
          url = await $api
            .GET(
              '/reports/owner-statement-summaries-per-layout/details/{contactId}/csv',
              {
                params: getOwnerStatementSummariesDetailQueryFilterParams({
                  filter: summariesDetailFilter,
                  dashboard,
                }),
              }
            )
            .then((res) => {
              if (res.error) throw res.error;
              return res.data.url;
            });
        } else {
          url = await $api
            .GET('/reports/owner-statement-summaries-per-layout/csv', {
              params: {
                query: getOwnerStatementSummariesQueryFilterParams(
                  summariesDetailFilter.monthRange
                ),
              },
            })
            .then((res) => {
              if (res.error) throw res.error;
              return res.data.url;
            });
        }
        if (!url) throw new Error('No CSV URL found');

        csv = {
          url: url,
          title: 'owner-statement-summaries',
        };
      } else if (activeTab === 'balance-sheet') {
        const res = await $api.GET('/reports/balance-sheet/csv', {
          params: {
            query: getBalanceSheetQueryFilterParams(balanceSheetFilter),
          },
        });
        if (res.error) throw res.error;
        csv = {
          url: res.data.url,
          title: 'balance-sheet',
        };
      }

      if (!csv) throw new Error('No report found');

      hideNotification({
        id: notifyId,
      });
      downloadFromUrl(csv.url, csv.title);
    } catch (error: any) {
      console.error(error);
      showApiErrorNotification({
        id: notifyId,
        error,
        title: "Couldn't download CSV",
        defaultMessage:
          'We failed to download the CSV. Please contact support if the issue persists.',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    download,
    loading,
    disabled: false,
  };
}

function useGetJournalEntriesCsvRows() {
  const modalWhere = useJournalEntriesModalWhere();
  const { filter: modalFilter } = useJournalEntriesModalFilter();
  const { filter: tableFilter } = useJournalEntriesTableFilter();

  const { mutateAsync: getJournalEntriesCsv } = useApiMutation(
    'get',
    '/reports/journal-entries/csv'
  );

  return useCallback(async () => {
    return await getJournalEntriesCsv({
      params: {
        query: getJournalEntriesApiQueryFilter({
          modalWhere,
          modalFilter,
          tableFilter,
          limit: undefined,
          page: undefined,
        }),
      },
    });
  }, [modalWhere, modalFilter, tableFilter, getJournalEntriesCsv]);
}
